/** biome-ignore-all lint/complexity/noBannedTypes: used intentionally to allow any non-null object */
/** biome-ignore-all lint/suspicious/noExplicitAny: any is needed here */

import type { FunctionComponent, JSX } from 'react';
import type { TargetingContextualSegmentCreateParams, TargetingInventoryQuality } from '../gqlTypes';
import type { ConstValues } from '../helpers';
import type { Option, OptionState, OptionsMap } from '../options';
import type { CustomVariablesFormState } from './advancedTargeting';
import type { AppInventoryVersionType } from './appInventory';
import type { AudienceData } from './audience';
import type { CategoryData } from './category';
import type { ContentObjectApi, ContentObjectOption } from './contentObject';
import type { CustomVariablesData, CustomVariablesDataPayload } from './customVariables';
import type { DomainData } from './domain';
import type { UrlTargeting } from './domains';
import type { FormatTypeData } from './formatType';
import type { GeographicTargetingApi } from './geographic';
import type {
  InventoryAndContentApi,
  InventoryAndContentApiRequest,
  TargetableInventoryList,
} from './inventoryAndContent';
import type { MetacategoryAPI } from './metacategory';
import type { RetargetingSegmentApiParams } from './retargeting';
import type { SelectRateDataViewability, SelectRateDataViewThroughRate, SelectRateFormState } from './selectRate';
import type { TargetingAppInventory } from './targetingAppInventoryTypes';
import type { RequiredTargetingFormType } from './targetingPresets';
import type { CriteriaValue } from './targetingValuesTypes';
import type { TechnologyAndDevicesData } from './technologyAndDevices';
import type { VideoData } from './video';

export type InterDimensionOperator = 'AND' | 'OR';

export interface TargetingDimension {
  inter_dimension_operator: InterDimensionOperator;
}

export type AttentionTargeting = '1' | null;
export type VPVHTargeting = 'true' | null;

export interface Targeting extends TargetingDimension {
  audience?: AudienceData | null;
  content?: InventoryAndContentApi | InventoryAndContentApiRequest | null;
  contextual_segment?: TargetingContextualSegmentCreateParams | null;
  custom?: CustomVariablesData | null;
  domain?: DomainData | null;
  app_inventory?: TargetingAppInventory | null;
  exclude_non_direct?: boolean | null;
  geographic?: GeographicTargetingApi | null;
  include_open_bidding_inventory?: '1' | null;
  inventory_format?: FormatTypeData | null;
  metacategory?: MetacategoryAPI | null;
  technographic?: TechnologyAndDevicesData | null;
  video?: VideoData | null;
  viewability?: SelectRateDataViewability | null;
  vtr?: SelectRateDataViewThroughRate | null;
  instream_content?: ContentObjectApi | null;
  inventory_quality?: TargetingInventoryQuality | null;
  rendering_context?: RequiredTargetingFormType | null;
  attention?: AttentionTargeting;
  retargeting_segment?: RetargetingSegmentApiParams | null;
  vpvh?: VPVHTargeting;
}

export const TargetingFields = {
  AD_SIZES: 'ads',
  DIMENSION: 'dimension',
  INVENTORY_AND_CONTENT: 'content',
  URL_TARGETING: 'url_targeting',
  DOMAIN: 'domain',
  APP_INVENTORY: 'app_inventory',
  CATEGORY: 'categories',
  GEOGRAPHIC: 'geographic',
  TECHNOLOGY_AND_DEVICES: 'technographic',
  EXCLUDE_INDIRECT: 'exclude_non_direct',
  INCLUDE_OPEN_BIDDING_INVENTORY: 'include_open_bidding_inventory',
  VIDEO: 'video',
  CONTEXTUAL_SEGMENTS: 'contextual',
  CUSTOM_VARIABLES: 'custom',
  ADVANCED_TARGETING: 'advanced_targeting',
  VIEWABILITY: 'viewability',
  VIEW_THROUGH_RATE: 'vtr',
  AUDIENCE: 'audience',
  FORMAT_TYPE: 'inventory_format',
  INVENTORY_FORMAT: 'rendering_context',
  EXCLUDE_MFA: 'exclude_mfa',
  METACATEGORY: 'metacategory',
  CONTENT_OBJECT: 'instream_content',
  SCHAIN_THRESHOLD: 'schain_threshold',
  INVENTORY_QUALITY: 'inventory_quality',
  ATTENTION: 'attention',
  RETARGETING: 'retargeting_segment',
  VPVH: 'vpvh',
} as const;
export type TargetingFieldsValues = ConstValues<typeof TargetingFields>;

export const ListIconType = {
  INCLUDE: 'INCLUDE',
  EXCLUDE: 'EXCLUDE',
} as const;
export type ListIconType = ConstValues<typeof ListIconType>;

export const AllowBlockType = {
  ALLOW: 'Allow',
  BLOCK: 'Block',
} as const;
export type AllowBlockType = ConstValues<typeof AllowBlockType>;

export const displayContexts = {
  TRAFFIC_SET: 'trafficSet',
  DEAL: 'deal',
  PACKAGE: 'package',
  PRICING_RULE: 'pricingRule',
} as const;

export type DisplayContextsType = ConstValues<typeof displayContexts>;

export type TargetingConfig = {
  name: string | JSX.Element;
  field: TargetingFieldsValues;
  TargetingComponent: FunctionComponent<TargetingProps<any, any>>;
  customOptions?: TargetingCustomOptions<any>;
  displayContext?: DisplayContextsType;
};

export interface TargetingBase<T, K> {
  name: string | JSX.Element;
  targetingParams: T | null;
  accountUid?: string;
  readonly?: boolean;
  isDisabled?: boolean;
  isRequired?: boolean;
  customOptions?: TargetingCustomOptions<any>;
  customValidationMethods?: K;
  isFromCurated?: boolean;
  displayContext?: DisplayContextsType;
  disabledReasonMessage?: string;
}

export interface TargetingProps<T, K = {}> extends TargetingBase<T, K> {
  onFieldUpdate?: (field: TargetingFieldsValues, newData: T | null, shouldValidate?: boolean) => void;
  field: TargetingFieldsValues;
  isNewFormat?: boolean;
  inventoryVersion?: AppInventoryVersionType;
}

export interface TargetingReadOnlyProps<T, K = {}> extends TargetingBase<T, K> {
  onOpenDrawerClick: (type?: string) => void;
  onRemoveClick?: () => void;
  dataTest?: string;
}

export interface TargetingState {
  [TargetingFields.EXCLUDE_INDIRECT]: boolean;
  [TargetingFields.METACATEGORY]: MetacategoryAPI | null;
  [TargetingFields.INCLUDE_OPEN_BIDDING_INVENTORY]: boolean;
  [TargetingFields.DIMENSION]: CriteriaValue;
  [TargetingFields.INVENTORY_AND_CONTENT]: InventoryAndContentApi | null;
  [TargetingFields.APP_INVENTORY]: TargetingAppInventory | null;
  [TargetingFields.URL_TARGETING]: UrlTargeting | null;
  [TargetingFields.CATEGORY]: CategoryData | null;
  [TargetingFields.GEOGRAPHIC]: GeographicTargetingApi | null;
  [TargetingFields.TECHNOLOGY_AND_DEVICES]: TechnologyAndDevicesData | null;
  [TargetingFields.VIDEO]: VideoData | null;
  [TargetingFields.VIEW_THROUGH_RATE]: SelectRateFormState | null;
  [TargetingFields.CONTEXTUAL_SEGMENTS]?: CustomVariablesDataPayload[];
  [TargetingFields.CUSTOM_VARIABLES]: CustomVariablesFormState | null;
  [TargetingFields.VIEWABILITY]: SelectRateFormState | null;
  [TargetingFields.FORMAT_TYPE]: FormatTypeData | null;
  [TargetingFields.AUDIENCE]: AudienceData | null;
  [TargetingFields.CONTENT_OBJECT]: ContentObjectApi | null;
  [TargetingFields.INVENTORY_FORMAT]: RequiredTargetingFormType | null;
  [TargetingFields.INVENTORY_QUALITY]: TargetingInventoryQuality | null;
  [TargetingFields.ATTENTION]: AttentionTargeting;
  [TargetingFields.RETARGETING]: RetargetingSegmentApiParams | null;
  [TargetingFields.VPVH]: VPVHTargeting;
}

export type TargetingCustomOptions<T> = {
  defaultOption: T;
  availableOptions: T[];
};

export interface InventoryFetchResponse {
  data: TargetableInventoryList | undefined;
  isLoading: boolean;
  refetch: unknown;
}
type OptionWithUid = Option & { uid?: string };
export type UseAccountOptionsFetch = () => {
  accounts: OptionState<OptionWithUid>;
};

export type OptionItem = { loading: boolean; options: OptionsMap };
export type UseOptionFetch = (type: ContentObjectOption, queryEnabled?: boolean) => OptionItem;

export type MarketProps = {
  name: string;
  id?: string;
};

export const SchainThresholdNodesOption = {
  ALL: '99999',
  ONE: '1',
  TWO: '2',
  THREE: '3',
} as const;
export type SchainThresholdNodes = ConstValues<typeof SchainThresholdNodesOption>;

export const IQ_TRUNCATE_SCORE = 30 as const;
export const IQ_SCORE_VAL = '30.00' as const;
